export const normalizeAmount = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

export const formatKes = (value) => {
  return `KES ${normalizeAmount(value).toLocaleString()}`;
};

export const toDateValue = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value?.toDate === 'function') {
    const converted = value.toDate();
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  if (typeof value?.seconds === 'number') {
    const converted = new Date(value.seconds * 1000);
    return Number.isNaN(converted.getTime()) ? null : converted;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatShortDate = (value, fallback = '—') => {
  const dateValue = toDateValue(value);

  if (!dateValue) {
    return fallback;
  }

  return dateValue.toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const formatDateTime = (value, fallback = '—') => {
  const dateValue = toDateValue(value);

  if (!dateValue) {
    return fallback;
  }

  return dateValue.toLocaleString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

export const getMonthKey = (value = new Date()) => {
  const dateValue = toDateValue(value) || new Date();
  const month = `${dateValue.getMonth() + 1}`.padStart(2, '0');
  return `${dateValue.getFullYear()}-${month}`;
};

export const getMonthLabel = (monthKey) => {
  if (!monthKey || typeof monthKey !== 'string' || !monthKey.includes('-')) {
    return 'Current month';
  }

  const [year, month] = monthKey.split('-').map(Number);
  const parsed = new Date(year, (month || 1) - 1, 1);

  if (Number.isNaN(parsed.getTime())) {
    return 'Current month';
  }

  return parsed.toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'long'
  });
};

export const getDateInputValue = (value = new Date()) => {
  const dateValue = toDateValue(value) || new Date();
  return new Date(dateValue.getTime() - dateValue.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
};

export const getDateTimeInputValue = (value = new Date()) => {
  const dateValue = toDateValue(value) || new Date();
  return new Date(dateValue.getTime() - dateValue.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 16);
};

const resolvePaymentDate = (payment) => {
  return toDateValue(payment.paymentDate) || toDateValue(payment.createdAt) || new Date();
};

const normalizeIncomeMarker = (value) => String(value || '').trim().toLowerCase();

export const isAdmissionFeeIncomeRecord = (payment = {}) => {
  const paymentCategory = normalizeIncomeMarker(payment.paymentCategory || payment.incomeCategory || payment.category || payment.type);
  if (paymentCategory === 'admission_fee' || paymentCategory === 'admission fee') {
    return true;
  }

  const sourceFields = [
    payment.source,
    payment.sourceCollection,
    payment.paymentSource,
    payment.recordSource,
    payment.description,
    payment.memo
  ];

  return sourceFields.some((value) => {
    const normalized = normalizeIncomeMarker(value);
    return (
      normalized.includes('admission_fee') ||
      normalized.includes('admission fee') ||
      normalized.includes('admissionfee')
    );
  });
};

export const filterSchoolFeeIncomeRecords = (records = []) => {
  return records.filter((record) => !isAdmissionFeeIncomeRecord(record));
};

export const resolveBillPaymentPeriodKey = (payment) => {
  return payment.billingPeriodKey || payment.billPeriodKey || getMonthKey(resolvePaymentDate(payment));
};

export const resolveRequisitionPaymentPeriodKey = (payment) => {
  return payment.paymentPeriodKey || payment.requisitionPeriodKey || getMonthKey(resolvePaymentDate(payment));
};

export const resolveSalaryPaymentPeriodKey = (payment) => {
  return payment.salaryPeriodKey || payment.payrollMonth || getMonthKey(resolvePaymentDate(payment));
};

export const parseMonthKey = (monthKey) => {
  if (!monthKey || typeof monthKey !== 'string' || !monthKey.includes('-')) {
    return null;
  }

  const [year, month] = monthKey.split('-').map(Number);
  if (!year || !month || month < 1 || month > 12) {
    return null;
  }

  return { year, month };
};

export const compareMonthKeys = (firstKey, secondKey) => {
  const first = parseMonthKey(firstKey);
  const second = parseMonthKey(secondKey);

  if (!first || !second) return 0;
  if (first.year !== second.year) return first.year - second.year;
  return first.month - second.month;
};

export const getEmployeeSalaryForPeriod = (employee, periodKey) => {
  const history = Array.isArray(employee?.salaryHistory) ? employee.salaryHistory : [];
  const applicableChanges = history
    .filter((change) => (
      change
      && parseMonthKey(change.effectiveFrom)
      && compareMonthKeys(change.effectiveFrom, periodKey) <= 0
    ))
    .sort((first, second) => {
      const monthOrder = compareMonthKeys(first.effectiveFrom, second.effectiveFrom);
      if (monthOrder !== 0) return monthOrder;
      const firstRecordedAt = toDateValue(first.recordedAt)?.getTime() || 0;
      const secondRecordedAt = toDateValue(second.recordedAt)?.getTime() || 0;
      return firstRecordedAt - secondRecordedAt;
    });

  if (applicableChanges.length === 0) {
    return normalizeAmount(employee?.salary);
  }

  const latestChange = applicableChanges[applicableChanges.length - 1];
  return normalizeAmount(latestChange.salary ?? latestChange.amount);
};

export const buildMonthKey = (year, month) => `${year}-${`${month}`.padStart(2, '0')}`;

export const getMonthRange = (startKey, endKey) => {
  if (compareMonthKeys(startKey, endKey) > 0) return [];

  const start = parseMonthKey(startKey);
  const end = parseMonthKey(endKey);
  if (!start || !end) return [];

  const months = [];
  let year = start.year;
  let month = start.month;

  while (year < end.year || (year === end.year && month <= end.month)) {
    months.push(buildMonthKey(year, month));
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }

  return months;
};

export const getPaymentTimestamp = (payment) => {
  return resolvePaymentDate(payment)?.getTime() || 0;
};

export const normalizeSalaryPaymentStatus = (payment) => {
  return String(payment?.paymentStatus || payment?.status || 'Pending').trim().toLowerCase();
};

export const isSuccessfulSalaryPayment = (payment) => {
  return ['paid', 'processed', 'completed', 'successful', 'success', 'succeeded'].includes(
    normalizeSalaryPaymentStatus(payment)
  );
};

export const isSuccessfulBillPayment = isSuccessfulSalaryPayment;

export const normalizeRequisitionPaymentStatus = (payment) => {
  const explicitStatus = payment?.paymentStatus || payment?.status;

  if (explicitStatus !== undefined && explicitStatus !== null && String(explicitStatus).trim() !== '') {
    return String(explicitStatus).trim().toLowerCase();
  }

  if (payment?.lotusTransactionId) {
    return 'pending';
  }

  return 'paid';
};

export const isSuccessfulRequisitionPayment = (payment) => {
  return ['paid', 'processed', 'completed', 'successful', 'success', 'succeeded'].includes(
    normalizeRequisitionPaymentStatus(payment)
  );
};

export const getSalaryPaymentAllocations = (payment) => {
  const allocationBreakdown = Array.isArray(payment.allocationBreakdown) ? payment.allocationBreakdown : [];

  if (allocationBreakdown.length > 0) {
    return allocationBreakdown
      .map((item) => {
        const periodKey = item.periodKey || resolveSalaryPaymentPeriodKey(payment);
        return {
          periodKey,
          periodLabel: item.periodLabel || getMonthLabel(periodKey),
          expectedAmount: normalizeAmount(item.expectedAmount || payment.expectedSalary || payment.amount),
          allocatedAmount: normalizeAmount(item.allocatedAmount || item.amount),
          paymentDate: payment.paymentDate || payment.createdAt || null
        };
      })
      .filter((item) => item.periodKey);
  }

  const periodKey = resolveSalaryPaymentPeriodKey(payment);
  return [{
    periodKey,
    periodLabel: payment.salaryPeriodLabel || getMonthLabel(periodKey),
    expectedAmount: normalizeAmount(payment.expectedSalary || payment.amount),
    allocatedAmount: normalizeAmount(payment.amount),
    paymentDate: payment.paymentDate || payment.createdAt || null
  }];
};

export const getBillPaymentAllocations = (payment) => {
  const allocationBreakdown = Array.isArray(payment.allocationBreakdown) ? payment.allocationBreakdown : [];

  if (allocationBreakdown.length > 0) {
    return allocationBreakdown
      .map((item) => {
        const periodKey = item.periodKey || resolveBillPaymentPeriodKey(payment);
        return {
          billId: item.billId || payment.billId || '',
          vendorId: item.vendorId || payment.vendorId || '',
          periodKey,
          periodLabel: item.periodLabel || getMonthLabel(periodKey),
          expectedAmount: normalizeAmount(item.expectedAmount || payment.expectedAmount || payment.amount),
          allocatedAmount: normalizeAmount(item.allocatedAmount || item.amount),
          remainingAmount: normalizeAmount(item.remainingAmount),
          paymentDate: payment.paymentDate || payment.createdAt || null
        };
      })
      .filter((item) => item.periodKey);
  }

  const periodKey = resolveBillPaymentPeriodKey(payment);
  return [{
    billId: payment.billId || '',
    vendorId: payment.vendorId || '',
    periodKey,
    periodLabel: payment.billingPeriodLabel || getMonthLabel(periodKey),
    expectedAmount: normalizeAmount(payment.expectedAmount || payment.amount),
    allocatedAmount: normalizeAmount(payment.amount),
    remainingAmount: normalizeAmount(payment.remainingAmount),
    paymentDate: payment.paymentDate || payment.createdAt || null
  }];
};

const getBalanceTone = (expectedAmount, paidAmount) => {
  if (expectedAmount <= 0) {
    return 'muted';
  }

  if (paidAmount <= 0) {
    return 'danger';
  }

  if (paidAmount >= expectedAmount) {
    return 'success';
  }

  return 'warning';
};

export const summarizeFixedBills = ({
  bills,
  vendorsById,
  branchesById,
  payments,
  periodKey
}) => {
  const confirmedPayments = (payments || []).filter((payment) => isSuccessfulBillPayment(payment));
  const billPaymentAllocations = confirmedPayments.flatMap((payment) => getBillPaymentAllocations(payment));

  return bills.map((bill) => {
    const vendor = vendorsById[bill.vendor] || {};
    const branchId = vendor.branch || bill.branch || 'General';
    const billPayments = billPaymentAllocations
      .filter(
        (allocation) =>
          allocation.billId === bill.id &&
          allocation.periodKey === periodKey
      )
      .sort((first, second) => {
        const firstTime = toDateValue(first.paymentDate)?.getTime() || 0;
        const secondTime = toDateValue(second.paymentDate)?.getTime() || 0;
        return secondTime - firstTime;
      });
    const expectedAmount = normalizeAmount(bill.billAmount);
    const paidAmount = billPayments.reduce((sum, payment) => sum + normalizeAmount(payment.allocatedAmount), 0);
    const remainingAmount = Math.max(expectedAmount - paidAmount, 0);

    return {
      ...bill,
      vendorName: vendor.vendorName || 'Vendor not found',
      vendorServices: vendor.vendorServices || '',
      vendorEmail: vendor.vendorEmail || '',
      vendorPhone: vendor.vendorPhone || '',
      serviceDesc: vendor.serviceDesc || '',
      paymentDetails: vendor.paymentDetails || '',
      branchId,
      branchName: branchId === 'General' ? 'General' : branchesById[branchId] || 'Campus not set',
      expectedAmount,
      paidAmount,
      remainingAmount,
      progress: expectedAmount > 0 ? Math.min((paidAmount / expectedAmount) * 100, 100) : 0,
      paymentTone: getBalanceTone(expectedAmount, paidAmount),
      paymentStatus:
        expectedAmount <= 0
          ? 'Amount not set'
          : remainingAmount <= 0
            ? 'Settled'
            : paidAmount > 0
              ? 'Partially paid'
              : 'Not paid',
      paymentCount: billPayments.length,
      lastPaymentDate: billPayments[0] ? toDateValue(billPayments[0].paymentDate) : null
    };
  });
};

export const summarizeRequisitions = ({
  requisitions,
  branchesById,
  payments
}) => {
  return requisitions.map((item) => {
    const requisitionPayments = payments
      .filter((payment) => isSuccessfulRequisitionPayment(payment))
      .filter((payment) => payment.requisitionId === item.id)
      .sort((first, second) => {
        const firstTime = resolvePaymentDate(first)?.getTime() || 0;
        const secondTime = resolvePaymentDate(second)?.getTime() || 0;
        return secondTime - firstTime;
      });
    const expectedAmount = normalizeAmount(item.totalAmount);
    const paidAmount = requisitionPayments.reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
    const remainingAmount = Math.max(expectedAmount - paidAmount, 0);

    return {
      ...item,
      branchName:
        item.selectedCampus === 'General'
          ? 'General'
          : branchesById[item.selectedCampus] || 'Campus not set',
      expectedAmount,
      paidAmount,
      remainingAmount,
      progress: expectedAmount > 0 ? Math.min((paidAmount / expectedAmount) * 100, 100) : 0,
      paymentTone: getBalanceTone(expectedAmount, paidAmount),
      paymentStatus:
        expectedAmount <= 0
          ? 'Amount not set'
          : remainingAmount <= 0
            ? 'Settled'
            : paidAmount > 0
              ? 'Partially paid'
              : 'Not paid',
      paymentCount: requisitionPayments.length,
      lastPaymentDate: requisitionPayments[0] ? resolvePaymentDate(requisitionPayments[0]) : null
    };
  });
};

const normalizeMarketingExpenseStatusValue = (value) => String(value || 'Pending').trim().toLowerCase();

export const normalizeMarketingExpenseStatus = (expense) => {
  return normalizeMarketingExpenseStatusValue(expense?.paymentStatus || expense?.status);
};

export const isSuccessfulMarketingExpense = (expense) => {
  return ['paid', 'processed', 'completed', 'successful', 'success', 'succeeded', 'confirmed'].includes(
    normalizeMarketingExpenseStatus(expense)
  );
};

export const summarizeMarketingExpenses = ({
  budgets,
  expenses
}) => {
  const expensesByBudgetId = (expenses || []).reduce((accumulator, expense) => {
    const budgetId = String(
      expense.budgetId
      || expense.marketingBudgetId
      || expense.budget
      || expense.budgetDocId
      || ''
    ).trim();

    if (!budgetId) {
      return accumulator;
    }

    if (!accumulator[budgetId]) {
      accumulator[budgetId] = [];
    }

    accumulator[budgetId].push(expense);
    return accumulator;
  }, {});

  return (budgets || []).map((budget) => {
    const budgetId = String(budget.docID || budget.id || '').trim();
    const budgetExpenses = [...(expensesByBudgetId[budgetId] || [])].sort((first, second) => {
      const firstTime = toDateValue(first.expenseDate || first.createdAt || first.updatedAt)?.getTime() || 0;
      const secondTime = toDateValue(second.expenseDate || second.createdAt || second.updatedAt)?.getTime() || 0;
      return secondTime - firstTime;
    });
    const paidExpenses = budgetExpenses.filter((expense) => isSuccessfulMarketingExpense(expense));
    const pendingExpenses = budgetExpenses.filter((expense) => {
      const normalizedStatus = normalizeMarketingExpenseStatus(expense);
      return !isSuccessfulMarketingExpense(expense) && normalizedStatus !== 'cancelled' && normalizedStatus !== 'canceled';
    });
    const budgetAmount = normalizeAmount(budget.amount);
    const paidAmount = paidExpenses.reduce((sum, expense) => sum + normalizeAmount(expense.expenseAmount || expense.amount), 0);
    const pendingAmount = pendingExpenses.reduce((sum, expense) => sum + normalizeAmount(expense.expenseAmount || expense.amount), 0);
    const committedAmount = paidAmount + pendingAmount;
    const remainingAmount = Math.max(budgetAmount - committedAmount, 0);

    return {
      ...budget,
      budgetAmount,
      paidAmount,
      pendingAmount,
      committedAmount,
      remainingAmount,
      expenseCount: budgetExpenses.length,
      paidCount: paidExpenses.length,
      pendingCount: pendingExpenses.length,
      paymentTone:
        budgetAmount <= 0
          ? 'muted'
          : paidAmount >= budgetAmount
            ? 'success'
            : committedAmount > 0
              ? 'warning'
              : 'muted',
      paymentStatus:
        budgetAmount <= 0
          ? 'Amount not set'
          : remainingAmount <= 0
            ? 'Settled'
            : paidAmount > 0
              ? 'Partially paid'
              : committedAmount > 0
                ? 'Pending payment'
                : 'Not started',
      lastExpenseDate: budgetExpenses[0]
        ? toDateValue(budgetExpenses[0].expenseDate || budgetExpenses[0].createdAt || budgetExpenses[0].updatedAt)
        : null
    };
  });
};

export const summarizePayroll = ({
  employees,
  payments,
  periodKey
}) => {
  return employees.map((employee) => {
    const employeePayments = payments
      .filter((payment) => payment.employeeId === employee.id && isSuccessfulSalaryPayment(payment))
      .sort((first, second) => {
        const firstTime = resolvePaymentDate(first)?.getTime() || 0;
        const secondTime = resolvePaymentDate(second)?.getTime() || 0;
        return secondTime - firstTime;
      });
    const periodAllocations = employeePayments
      .flatMap((payment) => getSalaryPaymentAllocations(payment))
      .filter((allocation) => allocation.periodKey === periodKey);
    const expectedAmount = getEmployeeSalaryForPeriod(employee, periodKey);
    const paidAmount = periodAllocations.reduce((sum, allocation) => sum + normalizeAmount(allocation.allocatedAmount), 0);
    const remainingAmount = Math.max(expectedAmount - paidAmount, 0);

    return {
      ...employee,
      expectedAmount,
      paidAmount,
      remainingAmount,
      progress: expectedAmount > 0 ? Math.min((paidAmount / expectedAmount) * 100, 100) : 0,
      paymentTone: getBalanceTone(expectedAmount, paidAmount),
      paymentStatus:
        expectedAmount <= 0
          ? 'Amount not set'
        : remainingAmount <= 0
            ? 'Settled'
            : paidAmount > 0
              ? 'Partially paid'
              : 'Not paid',
      paymentCount: employeePayments.length,
      lastPaymentDate: employeePayments[0] ? resolvePaymentDate(employeePayments[0]) : null
    };
  });
};

export const buildPayrollTimeline = ({
  employee,
  employeePayments,
  periodKey
}) => {
  const confirmedPayments = employeePayments.filter((payment) => isSuccessfulSalaryPayment(payment));
  const firstPaymentMonthKey = [...new Set(confirmedPayments.map((payment) => resolveSalaryPaymentPeriodKey(payment)))]
    .sort(compareMonthKeys)[0];

  // Count balances from the first recorded payment month for this employee.
  let effectiveStartKey = firstPaymentMonthKey || periodKey;

  if (compareMonthKeys(effectiveStartKey, periodKey) > 0) {
    effectiveStartKey = periodKey;
  }

  const paidByPeriod = confirmedPayments.reduce((accumulator, payment) => {
    getSalaryPaymentAllocations(payment).forEach((allocation) => {
      const paymentPeriodKey = allocation.periodKey;
      accumulator[paymentPeriodKey] = (accumulator[paymentPeriodKey] || 0) + normalizeAmount(allocation.allocatedAmount);
    });
    return accumulator;
  }, {});

  return getMonthRange(effectiveStartKey, periodKey).map((monthKey) => {
    const expectedAmount = getEmployeeSalaryForPeriod(employee, monthKey);
    const paidAmount = paidByPeriod[monthKey] || 0;
    const remainingAmount = Math.max(expectedAmount - paidAmount, 0);

    return {
      periodKey: monthKey,
      periodLabel: getMonthLabel(monthKey),
      expectedAmount,
      paidAmount,
      remainingAmount
    };
  });
};

export const buildPayrollAllocationPreview = ({ timeline, amount }) => {
  let remainingToAllocate = normalizeAmount(amount);
  if (remainingToAllocate <= 0) return [];

  const allocations = [];
  timeline.forEach((bucket) => {
    if (remainingToAllocate <= 0 || bucket.remainingAmount <= 0) return;

    const allocatedAmount = Math.min(bucket.remainingAmount, remainingToAllocate);
    allocations.push({
      ...bucket,
      allocatedAmount
    });
    remainingToAllocate -= allocatedAmount;
  });

  return allocations;
};

export const buildPayrollRows = ({
  employees,
  payments,
  periodKey
}) => {
  const paymentsByEmployeeId = payments.reduce((accumulator, payment) => {
    const employeeId = String(payment.employeeId || '').trim();
    if (!employeeId) return accumulator;

    if (!accumulator[employeeId]) {
      accumulator[employeeId] = [];
    }

    accumulator[employeeId].push(payment);
    return accumulator;
  }, {});

  return summarizePayroll({
    employees,
    payments,
    periodKey
  })
    .map((employee) => {
      const employeePayments = [...(paymentsByEmployeeId[employee.id] || [])].sort((first, second) => (
        getPaymentTimestamp(first) - getPaymentTimestamp(second)
      ));
      const timeline = buildPayrollTimeline({
        employee,
        employeePayments,
        periodKey
      });

      const currentBucket = timeline.find((bucket) => bucket.periodKey === periodKey) || {
        periodKey,
        periodLabel: getMonthLabel(periodKey),
        expectedAmount: employee.expectedAmount,
        paidAmount: employee.paidAmount,
        remainingAmount: employee.remainingAmount
      };

      const previousOutstanding = timeline
        .filter((bucket) => compareMonthKeys(bucket.periodKey, periodKey) < 0)
        .reduce((sum, bucket) => sum + bucket.remainingAmount, 0);

      const totalOutstanding = previousOutstanding + currentBucket.remainingAmount;
      const outstandingPeriods = timeline.filter((bucket) => bucket.remainingAmount > 0).length;

      return {
        ...employee,
        currentBucket,
        previousOutstanding,
        totalOutstanding,
        outstandingPeriods,
        timeline
      };
    })
    .sort((first, second) => {
      if (second.totalOutstanding !== first.totalOutstanding) {
        return second.totalOutstanding - first.totalOutstanding;
      }

      if (second.previousOutstanding !== first.previousOutstanding) {
        return second.previousOutstanding - first.previousOutstanding;
      }

      return second.currentBucket.remainingAmount - first.currentBucket.remainingAmount;
    });
};
